import { PropsWithChildren, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate } from 'react-router'

import { ConnectionSetupRoutes } from 'app/application/types'
import { Form } from 'app/common/presentation'
import {
  AvailableOrInstalledConnection,
  ConnectionConfig,
  ConnectionName,
  ShopifyShop,
  SupportedAccount,
  isInstalledConnection,
} from 'app/connections/types'

import { Authorization, type AuthorizationState } from '../authorization'
import { Accounts } from '../connect/steps/components/Accounts/Accounts'
import { mapToUrl } from '../connectionUrlMapper'
import { InputWithExampleFormRow } from './InputWithExampleFormRow'
import { SettingsProps } from './Settings'
import styles from './Settings.module.scss'
import { DangerZone, SettingsSection } from './components'
import { isConfigured } from './isConfigured'

export interface GenericSettingsProps extends SettingsProps, PropsWithChildren {
  connection: AvailableOrInstalledConnection
  connectionConfig: ConnectionConfig
  onConnectionConfigUpdate: (key: string, value: SupportedAccount[] | string) => void
}

type AccountsRow = { key: 'accounts'; value: SupportedAccount[] | undefined }
type GenericRow = { key: Exclude<string, 'accounts'>; value: string }

type RowData = AccountsRow | GenericRow

const isAccountsRow = (row: RowData): row is AccountsRow =>
  ['accounts', 'advertisers'].includes(row.key)

const hasExampleValue: ConnectionName[] = ['facebook', 'googleAdwords']

const getUrlParamValue = (key: 'adId' | 'campaignId') => {
  switch (key) {
    case 'adId':
      return '{{adset.id}}'
    case 'campaignId':
      return '{campaignid}'
  }
}

const GenericSettingsContent = ({
  connection,
  connectionConfig,
  children,
  onConnectionConfigUpdate: onConnectionConfigUpdate,
  onConnectionConfigValidationUpdate,
}: GenericSettingsProps) => {
  const { t } = useTranslation('connections')

  const [authorizationState, setAuthorizationState] = useState<AuthorizationState>('UNKOWN')

  useEffect(() => {
    const isValidConfig = () => {
      if (Object.values(connectionConfig).some((value) => !value)) return false

      if (connection.authConfig && !['OUTDATED_OR_VALID', 'VALID'].includes(authorizationState)) {
        return false
      }

      return true
    }

    onConnectionConfigValidationUpdate(isValidConfig())
  }, [
    authorizationState,
    onConnectionConfigValidationUpdate,
    connection.authConfig,
    connectionConfig,
  ])

  const handleOnChange = (key: string) => (value: string) => onConnectionConfigUpdate(key, value)

  const renderFormRow = (row: RowData) => {
    if (isAccountsRow(row)) {
      const selectedAccounts = row.value ?? []

      return <Accounts key={row.key} accounts={selectedAccounts} connectionName={connection.name} />
    } else {
      if (hasExampleValue.includes(connection.name)) {
        switch (row.key) {
          case 'adId':
          case 'campaignId':
            return (
              <InputWithExampleFormRow
                key={row.key}
                data={row}
                example={
                  <div>
                    https://yourdomain.com/landing?
                    <span className={styles.exampleHighlight}>{row.value}</span>=
                    {getUrlParamValue(row.key)}
                  </div>
                }
                exampleHint={t('setup.trackingUrlHint')}
                handleOnChange={handleOnChange(row.key)}
                connection={connection}
              />
            )
        }
      }

      return (
        <Form.InputFormRow
          key={row.key}
          description={t(`setup.${connection.name}.config.${row.key}.description`)}
          onChange={
            connection.readonly || connection.readonlyConfig.includes(row.key)
              ? undefined
              : handleOnChange(row.key)
          }
          placeholder={t(`setup.${connection.name}.config.${row.key}.title`)}
          title={t(`setup.${connection.name}.config.${row.key}.title`)}
          value={row.value}
        />
      )
    }
  }

  return (
    <>
      <SettingsSection
        contentClassName={styles.basicCard}
        description={t('setup.basics.description')}
        title={t('setup.basics.title')}
      >
        {connection.authConfig && (
          <Authorization
            connection={connection}
            onAuthorizationStateChanged={setAuthorizationState}
          />
        )}
        {(!connection.authConfig ||
          ['OUTDATED_OR_VALID', 'VALID'].includes(authorizationState)) && (
          <>
            {Object.entries(connectionConfig).map(
              ([key, value]: [string, SupportedAccount[] | string | ShopifyShop[]]) =>
                renderFormRow({ key, value } as RowData),
            )}
            {children}
          </>
        )}
      </SettingsSection>
      {isInstalledConnection(connection) && <DangerZone connection={connection} />}
    </>
  )
}

const GenericSettings = ({ connection, ...genericSettingProps }: GenericSettingsProps) => {
  if (connection.multiStepSetup) {
    if (!isInstalledConnection(connection)) {
      return <Navigate to={ConnectionSetupRoutes.SignIn} replace state={{ connection }} />
    }
    if (!isConfigured(connection)) {
      return (
        <Navigate
          to={`/connections/${mapToUrl(connection.name)}/connect/${ConnectionSetupRoutes.SelectAccounts}`}
          replace
          state={{ connection }}
        />
      )
    }
  }

  return <GenericSettingsContent connection={connection} {...genericSettingProps} />
}

export default GenericSettings
